import { useRef, useState } from 'react';
import './CardViewer.css';
import { humanCardName, getBaseCard, getCardVariant, RETIRED_VARIANTS, VARIANT_MESSAGES } from '../utils/cards';
import CardVariantOverlay from '../CardVariantOverlay';
import { GiftCardWarningModal } from './GiftCardWarningModal';
import { GiftCardModal } from './GiftCardModal';
import { createGiftCard, getDeck, KEYS } from '../utils/storage';

const CardViewer = ({ isOpen, card, onClose, onGiftCreated, onOpenRelatedDecks, giftsEnabled, cardImageFolder = 'smallcards' }) => {
    const DECK_ENABLED_VARIANTS = ['holo', 'antique', 'foil', 'blueprint', 'crayon', 'cosmic', 'cutesy'];
    const cardRef = useRef(null);
    const [warningModalOpen, setWarningModalOpen] = useState(false);
    const [giftCardId, setGiftCardId] = useState(null);

    if (!isOpen || !card) return null;

    let variant = getCardVariant(card);
    let cardImage = `./assets/${cardImageFolder}/${getBaseCard(card).toLowerCase()}.png`;
    let cardBackImage = `./assets/${cardImageFolder}/back1.png`;
    let cardName = humanCardName(card);
    let isRetiredVariant = variant && RETIRED_VARIANTS.includes(variant);
    const hasRelatedDeck = DECK_ENABLED_VARIANTS.includes(variant || '');

    // for debug we swap these out to test in-progress booster pack "variant"
    // variant = 'booster';
    // cardImage = './assets/misc/booster.png';
    // cardBackImage = './assets/misc/booster.png';
    // cardName = 'Tap to open!';

    const handleGiftCardClick = () => {
        setWarningModalOpen(true);
    };

    const handleConfirmGiftCard = () => {
        // Remove card from deck
        const deck = getDeck();
        if (!deck[card] || deck[card] <= 0) return;

        deck[card]--;
        if (deck[card] === 0) {
            delete deck[card];
        }
        localStorage.setItem(KEYS.DECK, JSON.stringify(deck));
        onGiftCreated?.();

        // Create gift card
        const newGiftCardId = createGiftCard(card);
        setGiftCardId(newGiftCardId);
        setWarningModalOpen(false);
    };

    const handleGiftCardModalClose = () => {
        setGiftCardId(null);
        onClose();
    };

    const handleOpenRelatedDecks = () => {
        if (!hasRelatedDeck) return;
        onClose();
        onOpenRelatedDecks?.(variant);
    };

    return (
        <div className='card-viewer'>
            <button className='card-viewer-overlay' onClick={onClose} aria-label='Close card viewer'></button>
            {/* key restarts CSS animations when a different card is opened */}
            <div key={card} className='card-viewer-motion'>
                <div
                    ref={cardRef}
                    className='card-viewer-card'
                >
                    <div className='card-viewer-face card-viewer-front'>
                        <div className="single">
                            <div className={`card ${variant ?? ''}`}>
                                {variant ? (
                                    <CardVariantOverlay
                                        variant={variant}
                                        imageSrc={cardImage}
                                        seed={card}
                                        alt={cardName}
                                        title={cardName}
                                        className='card-viewer-image'
                                    />
                                ) : (
                                    <img alt={cardName} className='card-viewer-image' src={cardImage} />
                                )}
                            </div>
                        </div>
                        <p className='card-viewer-label'>{cardName}</p>
                        {/* if message */}
                        { VARIANT_MESSAGES[variant] ? (
                            <p className='card-viewer-retired-warning'>{VARIANT_MESSAGES[variant]}</p>
                        ) : isRetiredVariant ? (
                            <p className='card-viewer-retired-warning'>Discontinued Variant</p>
                        ) : null}
                    </div>

                    <div className='card-viewer-face card-viewer-back'>
                        <div className="single">
                            <div className={`card ${variant ?? ''}`}>
                                {variant ? (
                                    <CardVariantOverlay
                                        variant={variant}
                                        imageSrc={cardBackImage}
                                        seed={card}
                                        faceDown
                                        alt='card back'
                                        title={cardName}
                                        className='card-viewer-image'
                                    />
                                ) : (
                                    <img alt='card back' className='card-viewer-image' src={cardBackImage} />
                                )}
                            </div>
                        </div>
                        <p className='card-viewer-label'>{cardName}</p>
                        { isRetiredVariant && (
                            <p className='card-viewer-retired-warning'>Discontinued Variant</p>
                        )}
                    </div>

                    <div className='card-viewer-glare' />
                </div>

                {giftsEnabled && (
                    <div className='card-viewer-actions'>
                        <button className='gift-card-btn' onClick={handleGiftCardClick} aria-label='Convert to gift card'>
                            🎁 Gift this card
                        </button>
                        {hasRelatedDeck && (
                            <button className='related-deck-btn' onClick={handleOpenRelatedDecks} aria-label='Open related deck vault'>
                                More Like This
                            </button>
                        )}
                    </div>
                )}
            </div>

            <GiftCardWarningModal
                isOpen={warningModalOpen}
                onClose={() => setWarningModalOpen(false)}
                card={card}
                cardImageFolder={cardImageFolder}
                onConfirm={handleConfirmGiftCard}
            />

            <GiftCardModal
                isOpen={giftCardId !== null}
                onClose={handleGiftCardModalClose}
                giftCardId={giftCardId}
                card={card}
            />
        </div>
    );
};

export default CardViewer;